/**
 * Warm engine answers for the workbench's first calls, so Paige's route loader can ask the engine
 * for what the App will ask the moment it mounts (the job list, the job's latest batch overview) and
 * the dashboard stays until those answers are in hand (IMPLEMENTATION.md, sell-ready sprint 2, row 13).
 *
 * The adapter's `engineCall` takes a warm answer instead of fetching when one is waiting for the
 * same channel, command and arguments. An answer is kept for a short while after it settles, then
 * forgotten: this is a cache with a life, not a rendering delay. A failed warm-up is forgotten at
 * once so the App's own call fails or succeeds on its own.
 */
export interface WarmChannel {
  engineOrigin: string;
  tenant?: string | undefined;
}

const warm = new Map<string, Promise<unknown>>();
const WARM_LIFE_MS = 5_000;

export function warmKey(
  channel: WarmChannel,
  command: string,
  args: Record<string, unknown>,
): string {
  return JSON.stringify([channel.engineOrigin, channel.tenant ?? '', command, args]);
}

/** Starts the call now (through `fetch`) and keeps the promise for the adapter to take. */
export function warmEngine<T>(
  channel: WarmChannel,
  command: string,
  args: Record<string, unknown>,
  fetchAnswer: () => Promise<T>,
): Promise<T> {
  const key = warmKey(channel, command, args);
  const waiting = warm.get(key);
  if (waiting) return waiting as Promise<T>;
  const promise = fetchAnswer();
  warm.set(key, promise);
  promise.then(
    () => setTimeout(() => forget(key, promise), WARM_LIFE_MS),
    () => forget(key, promise),
  );
  return promise;
}

/** The warm answer for this call, if one is waiting; the adapter takes it in place of a fetch. */
export function takeWarmAnswer<T>(
  channel: WarmChannel,
  command: string,
  args: Record<string, unknown>,
): Promise<T> | undefined {
  return warm.get(warmKey(channel, command, args)) as Promise<T> | undefined;
}

/** For tests: every warm answer forgotten. */
export function forgetWarmAnswers(): void {
  warm.clear();
}

function forget(key: string, promise: Promise<unknown>): void {
  if (warm.get(key) === promise) warm.delete(key);
}
